import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { 
  Mail, Plus, Trash2, Edit2, Search, Send, 
  CheckCircle, Clock, XCircle, Settings, Timer, CalendarDays, User,
  ChevronLeft, ChevronRight
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

// Helper for authenticated API calls
const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

export default function RemindersPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Calendar date selection (defaults to today)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [datesWithReminders, setDatesWithReminders] = useState([]);
  
  // Clinic settings
  const [clinicSettings, setClinicSettings] = useState({
    clinic_name: "Tallinna Endokrinoloogia Kliinik",
    clinic_address: "www.endokrinoloog.ee",
    default_message: `Tere!
Teil peaks olema järgmisel kuul endokrinoloogi/ endokriinõe vastuvõtt. Ärge unustage broneerida aega!
Aja saate telefonil 5566 5451 / 5566 5135 või kodulehelt https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik
NB! Digiregistratuuris valige kindlasti spetsialisti nimi ja linn!

---

Здравствуйте!
В следующем месяце у Вас должен быть приём у эндокринолога / эндокринной медсестры. Не забудьте записаться на приём!
Записаться можно по телефону 5566 5451 / 5566 5135 или на сайте https://veebiregistratuur.ee/tallinna-endokrinoloogia-kliinik
Внимание! В электронной регистратуре обязательно выберите имя специалиста и город!`
  });
  
  // Reminder Dialog
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    patient_name: "",
    patient_surname: "",
    patient_id: "",
    patient_email: "",
    patient_phone: "",
    reminder_date: "",
    clinic_name: "",
    clinic_address: "",
    message: "",
  });
  
  // Settings Dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Monthly stats
  const [monthlyStats, setMonthlyStats] = useState({ total: 0, sent: 0, pending: 0, failed: 0 });
  const [statsMonth, setStatsMonth] = useState(new Date().getMonth() + 1);
  const [statsYear, setStatsYear] = useState(new Date().getFullYear());

  const MONTH_NAMES = ['Jaanuar', 'Veebruar', 'Märts', 'Aprill', 'Mai', 'Juuni', 'Juuli', 'August', 'September', 'Oktoober', 'November', 'Detsember'];

  // Scheduler status
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Reminder message template
  const [messageTemplate, setMessageTemplate] = useState("");
  const [canEditTemplate, setCanEditTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateDraft, setTemplateDraft] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Format date to DD.MM.YYYY
  const formatDateEE = (date) => {
    const d = date.getDate().toString().padStart(2, '0');
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const y = date.getFullYear();
    return `${d}.${m}.${y}`;
  };

  // Parse DD.MM.YYYY to Date
  const parseDateEE = (str) => {
    const [d, m, y] = str.split('.');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  };

  useEffect(() => {
    fetchClinicSettings();
    fetchSchedulerStatus();
    fetchDatesWithReminders();
    fetchMessageTemplate();
  }, []);

  // Re-fetch reminders when selected date changes
  useEffect(() => {
    if (selectedDate) {
      fetchReminders(formatDateEE(selectedDate));
    }
  }, [selectedDate]);

  // Re-fetch monthly stats when statsMonth/statsYear changes
  useEffect(() => {
    fetchMonthlyStats(statsMonth, statsYear);
  }, [statsMonth, statsYear]);

  const fetchReminders = async (createdDate) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (createdDate) params.set("created_date", createdDate);
      const response = await authFetch(`${API}/reminders?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setReminders(data);
      }
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
      toast.error("Meeldetuletuste laadimine ebaõnnestus");
    } finally {
      setLoading(false);
    }
  };

  const fetchDatesWithReminders = async () => {
    try {
      const response = await authFetch(`${API}/reminders/dates`);
      if (response.ok) {
        const dates = await response.json();
        setDatesWithReminders(dates.map(d => parseDateEE(d)));
      }
    } catch (error) {
      console.error("Failed to fetch reminder dates:", error);
    }
  };

  const fetchClinicSettings = async () => {
    try {
      const response = await authFetch(`${API}/settings/clinic`);
      if (response.ok) {
        const data = await response.json();
        setClinicSettings(data);
      }
    } catch (error) {
      console.error("Failed to fetch clinic settings:", error);
    }
  };

  const fetchMessageTemplate = async () => {
    try {
      const res = await authFetch(`${API}/reminders/reminder-template`);
      if (res.ok) {
        const data = await res.json();
        setMessageTemplate(data.message || "");
        setCanEditTemplate(!!data.can_edit);
      }
    } catch { /* */ }
  };

  const saveMessageTemplate = async () => {
    setSavingTemplate(true);
    try {
      const res = await authFetch(`${API}/reminders/reminder-template`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: templateDraft }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Viga");
      }
      setMessageTemplate(templateDraft);
      setEditingTemplate(false);
      toast.success("Sõnum salvestatud!");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await authFetch(`${API}/scheduler/status`);
      if (response.ok) {
        const data = await response.json();
        setSchedulerStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch scheduler status:", error);
    }
  };

  const fetchMonthlyStats = async (month, year) => {
    try {
      const response = await authFetch(`${API}/reminders/stats/monthly?month=${month}&year=${year}`);
      if (response.ok) {
        const data = await response.json();
        setMonthlyStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch monthly stats:", error);
    }
  };

  const handleReminderSubmit = async (e) => {
    e.preventDefault();
    
    // Auto-prepend +372 to phone if digits only
    const formData = { ...reminderForm };
    if (formData.patient_phone) {
      let phone = formData.patient_phone.replace(/\s/g, '');
      if (!phone.startsWith('+')) {
        phone = '+372' + phone;
      } else if (phone.startsWith('372')) {
        phone = '+' + phone;
      }
      formData.patient_phone = phone;
    }
    // Convert empty email to null
    if (!formData.patient_email || !formData.patient_email.trim()) {
      formData.patient_email = null;
    }
    
    try {
      const url = editingReminder 
        ? `${API}/reminders/${editingReminder.reminder_id}`
        : `${API}/reminders`;
      
      const response = await authFetch(url, {
        method: editingReminder ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingReminder ? "Meeldetuletus uuendatud" : "Meeldetuletus lisatud", { duration: 1500 });
        setReminderDialogOpen(false);
        setEditingReminder(null);
        resetReminderForm();
        fetchReminders(formatDateEE(selectedDate));
        fetchDatesWithReminders();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Salvestamine ebaõnnestus");
      }
    } catch (error) {
      console.error("Failed to save reminder:", error);
      toast.error("Salvestamine ebaõnnestus");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm("Kas olete kindel, et soovite selle meeldetuletuse kustutada?")) return;
    
    try {
      const response = await authFetch(`${API}/reminders/${reminderId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Meeldetuletus kustutatud");
        fetchReminders(formatDateEE(selectedDate));
        fetchDatesWithReminders();
      } else {
        toast.error("Kustutamine ebaõnnestus");
      }
    } catch (error) {
      console.error("Failed to delete reminder:", error);
      toast.error("Kustutamine ebaõnnestus");
    }
  };

  const handleSendReminder = async (reminderId) => {
    try {
      const response = await authFetch(`${API}/reminders/${reminderId}/send`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchReminders(formatDateEE(selectedDate));
      } else {
        const error = await response.json();
        toast.error(error.detail || "Saatmine ebaõnnestus");
      }
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error("Saatmine ebaõnnestus");
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await authFetch(`${API}/settings/clinic`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(clinicSettings),
      });

      if (response.ok) {
        toast.success("Seaded salvestatud");
        setSettingsDialogOpen(false);
      } else {
        toast.error("Seadete salvestamine ebaõnnestus");
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Seadete salvestamine ebaõnnestus");
    }
  };

  const resetReminderForm = () => {
    setReminderForm({
      patient_name: "",
      patient_surname: "",
      patient_id: "",
      patient_email: "",
      patient_phone: "",
      reminder_date: "",
      clinic_name: clinicSettings.clinic_name,
      clinic_address: clinicSettings.clinic_address,
      message: clinicSettings.default_message,
    });
  };

  const openNewReminderDialog = () => {
    setEditingReminder(null);
    setReminderForm({
      patient_name: "",
      patient_surname: "",
      patient_id: "",
      patient_email: "",
      patient_phone: "",
      reminder_date: "",
      clinic_name: clinicSettings.clinic_name,
      clinic_address: clinicSettings.clinic_address,
      message: clinicSettings.default_message,
    });
    setReminderDialogOpen(true);
  };

  const openEditDialog = (reminder) => {
    setEditingReminder(reminder);
    setReminderForm({
      patient_name: reminder.patient_name,
      patient_surname: reminder.patient_surname,
      patient_id: reminder.patient_id,
      patient_email: reminder.patient_email || "",
      patient_phone: reminder.patient_phone || "",
      reminder_date: reminder.reminder_date,
      clinic_name: reminder.clinic_name,
      clinic_address: reminder.clinic_address,
      message: reminder.message,
    });
    setReminderDialogOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Filter reminders by search query only (date filtering is done server-side)
  const filteredReminders = reminders.filter((reminder) => {
    const query = searchQuery.toLowerCase();
    return (
      reminder.patient_name.toLowerCase().includes(query) ||
      reminder.patient_surname.toLowerCase().includes(query) ||
      reminder.patient_id.includes(query) ||
      (reminder.patient_email || '').toLowerCase().includes(query) ||
      (reminder.patient_phone || '').includes(query)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "sent":
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Saadetud</Badge>;
      case "failed":
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" /> Ebaõnnestunud</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Ootel</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="reminders-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} settingsAction={() => setSettingsDialogOpen(true)} />

      <div className="md:ml-64 min-h-screen">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Patsiendi meeldetuletus</h1>
                <p className="text-sm text-slate-500">{reminders.length} meeldetuletust</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="icon" data-testid="settings-btn">
                    <Settings className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Kliiniku seaded</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Kliiniku nimi</Label>
                    <Input
                      value={clinicSettings.clinic_name}
                      onChange={(e) => setClinicSettings({ ...clinicSettings, clinic_name: e.target.value })}
                      placeholder="Kliiniku nimi"
                      data-testid="settings-clinic-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Kliiniku aadress</Label>
                    <Input
                      value={clinicSettings.clinic_address}
                      onChange={(e) => setClinicSettings({ ...clinicSettings, clinic_address: e.target.value })}
                      placeholder="Aadress"
                      data-testid="settings-clinic-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Vaikimisi sõnum</Label>
                    <Textarea
                      value={clinicSettings.default_message}
                      onChange={(e) => setClinicSettings({ ...clinicSettings, default_message: e.target.value })}
                      placeholder="Sõnum patsientidele"
                      rows={3}
                      data-testid="settings-default-message"
                    />
                  </div>
                  <Button onClick={handleSaveSettings} className="w-full bg-slate-900 hover:bg-slate-800">
                    Salvesta seaded
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button onClick={openNewReminderDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-reminder-btn">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Lisa meeldetuletus</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Scheduler Status */}
        {schedulerStatus && (
          <Card className="border-slate-200 bg-slate-50" data-testid="scheduler-status-card">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${schedulerStatus.running ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <Timer className={`w-4 h-4 ${schedulerStatus.running ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Automaatne saatmine: {schedulerStatus.running ? 'Aktiivne' : 'Peatatud'}
                    </p>
                    <p className="text-xs text-slate-500">
                      Iga päev kell 08:00 (Eesti aeg)
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-xs text-slate-500">
                  {schedulerStatus.next_run && (
                    <span data-testid="scheduler-next-run">
                      Järgmine: {new Date(schedulerStatus.next_run).toLocaleString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {schedulerStatus.last_run?.ran_at && (
                    <>
                      <span className="hidden sm:inline text-slate-300">|</span>
                      <span data-testid="scheduler-last-run">
                        Viimane: {new Date(schedulerStatus.last_run.ran_at).toLocaleString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {' '}({schedulerStatus.last_run.sent} saadetud, {schedulerStatus.last_run.failed} ebaõnnestunud)
                      </span>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Calendar + Reminders Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
          {/* Calendar Sidebar */}
          <div className="space-y-4">
            <Card className="border-slate-200" data-testid="calendar-card">
              <CardContent className="p-3">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  modifiers={{ hasReminders: datesWithReminders }}
                  modifiersStyles={{
                    hasReminders: {
                      fontWeight: 'bold',
                      textDecoration: 'underline',
                      textDecorationColor: '#3b82f6',
                      textUnderlineOffset: '3px',
                    }
                  }}
                  className="rounded-md"
                />
                <div className="px-2 pt-2 border-t border-slate-100 mt-2">
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <span className="font-bold underline decoration-blue-500 underline-offset-2">00</span> = kuupäev sisaldab patsiente
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Stats for selected date */}
            <Card className="border-slate-200">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-medium text-slate-700">
                  <CalendarDays className="w-4 h-4 inline mr-1" />
                  {formatDateEE(selectedDate)}
                </p>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-slate-900">{reminders.length}</p>
                    <p className="text-xs text-slate-500">Kokku</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-emerald-700">{reminders.filter(r => r.status === "sent").length}</p>
                    <p className="text-xs text-slate-500">Saadetud</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-amber-700">{reminders.filter(r => r.status === "pending").length}</p>
                    <p className="text-xs text-slate-500">Ootel</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-red-700">{reminders.filter(r => r.status === "failed").length}</p>
                    <p className="text-xs text-slate-500">Ebaõnnestunud</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monthly stats */}
            <Card className="border-slate-200" data-testid="monthly-stats-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      if (statsMonth === 1) {
                        setStatsMonth(12);
                        setStatsYear(statsYear - 1);
                      } else {
                        setStatsMonth(statsMonth - 1);
                      }
                    }}
                    className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
                    data-testid="stats-prev-month"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <p className="text-sm font-medium text-slate-700">
                    <CalendarDays className="w-4 h-4 inline mr-1" />
                    {MONTH_NAMES[statsMonth - 1]} {statsYear}
                  </p>
                  <button
                    onClick={() => {
                      const now = new Date();
                      const maxMonth = now.getMonth() + 1; // 1-based
                      const maxYear = now.getFullYear() + 1;
                      const isAtMax = statsMonth === maxMonth && statsYear === maxYear;
                      if (!isAtMax) {
                        if (statsMonth === 12) {
                          setStatsMonth(1);
                          setStatsYear(statsYear + 1);
                        } else {
                          setStatsMonth(statsMonth + 1);
                        }
                      }
                    }}
                    className={`p-1 rounded transition-colors ${
                      (() => {
                        const now = new Date();
                        const maxMonth = now.getMonth() + 1;
                        const maxYear = now.getFullYear() + 1;
                        return statsMonth === maxMonth && statsYear === maxYear;
                      })()
                        ? 'text-slate-200 cursor-default'
                        : 'hover:bg-slate-100 text-slate-500 hover:text-slate-900'
                    }`}
                    disabled={(() => {
                      const now = new Date();
                      const maxMonth = now.getMonth() + 1;
                      const maxYear = now.getFullYear() + 1;
                      return statsMonth === maxMonth && statsYear === maxYear;
                    })()}
                    data-testid="stats-next-month"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-slate-900">{monthlyStats.total}</p>
                    <p className="text-xs text-slate-500">Kokku</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-emerald-700">{monthlyStats.sent}</p>
                    <p className="text-xs text-slate-500">Saadetud</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-amber-700">{monthlyStats.pending}</p>
                    <p className="text-xs text-slate-500">Ootel</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2">
                    <p className="text-lg font-bold text-red-700">{monthlyStats.failed}</p>
                    <p className="text-xs text-slate-500">Ebaõnnestunud</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reminders List */}
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Otsi nime, isikukoodi või e-posti järgi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="reminder-search"
              />
            </div>

            {/* List */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 spinner" />
              </div>
            ) : filteredReminders.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {searchQuery ? "Meeldetuletusi ei leitud" : `${formatDateEE(selectedDate)} ei ole patsiente sisestatud`}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {searchQuery 
                      ? "Proovige muuta otsingut" 
                      : "Valige kalendrist teine kuupäev või lisage uus meeldetuletus"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={openNewReminderDialog} className="bg-slate-900 hover:bg-slate-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Lisa meeldetuletus
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredReminders.map((reminder) => (
                  <Card key={reminder.reminder_id} className="border-slate-200" data-testid={`reminder-card-${reminder.reminder_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg text-slate-900">
                              {reminder.patient_name} {reminder.patient_surname}
                            </h3>
                            {getStatusBadge(reminder.status)}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                            <p><span className="text-slate-400">Isikukood:</span> {reminder.patient_id}</p>
                            {reminder.patient_email && (
                              <p><span className="text-slate-400">E-post:</span> {reminder.patient_email}</p>
                            )}
                            {reminder.patient_phone && (
                              <p><span className="text-slate-400">Telefon:</span> {reminder.patient_phone}</p>
                            )}
                            <p><span className="text-slate-400">Saatmise kuupäev:</span> {reminder.reminder_date}</p>
                            <p><span className="text-slate-400">Kliinik:</span> {reminder.clinic_name}</p>
                          </div>
                          {reminder.created_by_email && (
                            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Sisestaja: {reminder.created_by_email}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {reminder.status === "pending" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSendReminder(reminder.reminder_id)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              data-testid={`send-reminder-${reminder.reminder_id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Saada
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(reminder)}
                            data-testid={`edit-reminder-${reminder.reminder_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteReminder(reminder.reminder_id)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-reminder-${reminder.reminder_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={(open) => {
        setReminderDialogOpen(open);
        if (!open) {
          setEditingReminder(null);
          resetReminderForm();
        }
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReminder ? "Muuda meeldetuletust" : "Lisa meeldetuletus"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReminderSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eesnimi *</Label>
                <Input
                  value={reminderForm.patient_name}
                  onChange={(e) => setReminderForm({ ...reminderForm, patient_name: e.target.value })}
                  placeholder="Eesnimi"
                  required
                  data-testid="reminder-patient-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Perekonnanimi *</Label>
                <Input
                  value={reminderForm.patient_surname}
                  onChange={(e) => setReminderForm({ ...reminderForm, patient_surname: e.target.value })}
                  placeholder="Perekonnanimi"
                  required
                  data-testid="reminder-patient-surname"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Isikukood (11 numbrit) *</Label>
              <Input
                value={reminderForm.patient_id}
                onChange={(e) => setReminderForm({ ...reminderForm, patient_id: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                placeholder="39901011234"
                required
                maxLength={11}
                data-testid="reminder-patient-id"
              />
            </div>
            
            <div className="space-y-2">
              <Label>E-posti aadress</Label>
              <Input
                value={reminderForm.patient_email}
                onChange={(e) => setReminderForm({ ...reminderForm, patient_email: e.target.value })}
                placeholder="patsient@email.ee"
                data-testid="reminder-patient-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Telefoninumber *</Label>
              <div className="flex">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-slate-200 bg-slate-50 text-slate-500 text-sm">+372</span>
                <Input
                  type="tel"
                  value={reminderForm.patient_phone}
                  onChange={(e) => setReminderForm({ ...reminderForm, patient_phone: e.target.value.replace(/[^\d]/g, '').slice(0, 8) })}
                  placeholder="56123456"
                  required
                  maxLength={8}
                  className="rounded-l-none"
                  data-testid="reminder-patient-phone"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Meeldetuletuse kuupäev (PP.KK.AAAA) *</Label>
              <Input
                value={reminderForm.reminder_date}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d.]/g, '');
                  // Auto-format as DD.MM.YYYY
                  if (value.length === 2 && !value.includes('.')) value += '.';
                  if (value.length === 5 && value.split('.').length === 2) value += '.';
                  setReminderForm({ ...reminderForm, reminder_date: value.slice(0, 10) });
                }}
                placeholder="15.03.2026"
                required
                maxLength={10}
                data-testid="reminder-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Kliiniku nimi</Label>
              <Input
                value={reminderForm.clinic_name}
                onChange={(e) => setReminderForm({ ...reminderForm, clinic_name: e.target.value })}
                placeholder="Kliiniku nimi"
                data-testid="reminder-clinic-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Kliiniku aadress</Label>
              <Input
                value={reminderForm.clinic_address}
                onChange={(e) => setReminderForm({ ...reminderForm, clinic_address: e.target.value })}
                placeholder="Aadress"
                data-testid="reminder-clinic-address"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Sõnum</Label>
              <Textarea
                value={reminderForm.message}
                onChange={(e) => setReminderForm({ ...reminderForm, message: e.target.value })}
                placeholder="Meeldetuletuse sõnum patsientile"
                rows={3}
                data-testid="reminder-message"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setReminderDialogOpen(false);
                  setEditingReminder(null);
                  resetReminderForm();
                }}
              >
                Tühista
              </Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="reminder-submit-btn">
                {editingReminder ? "Uuenda" : "Lisa"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      </div>
      <BottomNav />
    </div>
  );
}
